import { Spinner } from "@/components/ui/v2/spinner";
import { companyDocumentsApi } from "@/services/(company)/api.documents";
import { store } from "@/store";
import {
  CompanyDocsPage,
  CompanyDocumentDetail,
  CompanyDocumentForm,
  CompanyDocumentRequestForm,
  CompanyDocumentRequestView,
  CompanyDocuments,
  CompanyDocumentUploadForm,
  CompanyEmployeeDocsPage,
  CompanyEmployeeDocumentsDetails,
  CompanyReceivedRequestsPage,
  CompanySentRequestsPage,
  CompanyDocumentSettingsPage,
} from "@/utils/lazyLoadComponents";
import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_DOCUMENTS_DYNAMIC_ID_KEY = "id";

export enum COMPANY_DOCUMENTS_PATH_SEGMENTS {
  ROOT = "documents",
  EMPLOYEE_DOCUMENTS = "employee-documents",
  COMPANY_DOCUMENTS = "company-documents",
  SENT_REQUESTS = "sent-requests",
  RECEIVED_REQUESTS = "recieved-requests",
  REQUEST = "request",
  UPLOAD = "upload",
  DETAILS = `:${COMPANY_DOCUMENTS_DYNAMIC_ID_KEY}`,
  EDIT = "edit",
  VIEW = "view",
  NEW = "new",
  // Module settings (moved out of Company Settings)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyDocumentsRoutes: RouteObject = {
  path: COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Documents" },
  HydrateFallback: Spinner,
  // No Component here — only the 4 tab index pages get the page-level tabs layout
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyDocuments,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_DOCUMENTS_PATHS.EMPLOYEE_DOCUMENTS),
        },
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
          handle: { breadcrumb: "Employee Documents" },
          Component: CompanyEmployeeDocsPage,
        },
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
          handle: { breadcrumb: "Company Documents" },
          Component: CompanyDocsPage,
        },
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
          handle: { breadcrumb: "Sent Requests" },
          Component: CompanySentRequestsPage,
        },
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
          handle: { breadcrumb: "Received Requests" },
          Component: CompanyReceivedRequestsPage,
        },
      ],
    },

    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks any dynamic sibling in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Documents Settings" },
      Component: CompanyDocumentSettingsPage,
    },

    // ── Employee documents detail routes (no stats, no tabs) ─────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
      handle: { breadcrumb: "Employee Documents" },
      children: [
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.NEW,
          handle: { breadcrumb: "Add New Document" },
          Component: CompanyDocumentForm,
        },
        {
          path: `${COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit Document" },
          Component: CompanyDocumentForm,
        },
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          loader: async ({ params }) => {
            const id = params[COMPANY_DOCUMENTS_DYNAMIC_ID_KEY];
            if (!id) return { breadcrumbLabel: "Document Details" };
            const result = await store.dispatch(
              companyDocumentsApi.endpoints.companyGetEmployeeDocumentsById.initiate(
                { id },
              ),
            );
            const name =
              result.data?.data?.user?.firstName &&
              result.data?.data?.user?.lastName
                ? `${result.data?.data?.user?.firstName} ${result.data?.data?.user?.lastName}`
                : (result.data?.data?.user?.firstName ?? `Document #${id}`);
            return { breadcrumbLabel: name };
          },
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Document Details",
            title: "Document Details",
          },
          Component: CompanyEmployeeDocumentsDetails,
        },
      ],
    },

    // ── Company documents detail routes (no stats, no tabs) ───────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
      handle: { breadcrumb: "Company Documents" },
      children: [
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.NEW,
          handle: { breadcrumb: "Add New Document" },
          Component: CompanyDocumentForm,
        },
        {
          path: `${COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${COMPANY_DOCUMENTS_PATH_SEGMENTS.VIEW}`,
          handle: { breadcrumb: "View Document" },
          Component: CompanyDocumentDetail,
        },
        {
          path: `${COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: CompanyDocumentForm,
        },
      ],
    },

    // ── Sent requests detail routes (no stats, no tabs) ───────────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
      handle: { breadcrumb: "Sent Requests" },
      children: [
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Request" },
          Component: CompanyDocumentRequestView,
        },
        {
          path: `${COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: CompanyDocumentRequestForm,
        },
      ],
    },

    // ── Received requests detail routes (no stats, no tabs) ───────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
      handle: { breadcrumb: "Received Requests" },
      children: [
        {
          path: COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Request" },
          Component: CompanyDocumentRequestView,
        },
        {
          path: `${COMPANY_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${COMPANY_DOCUMENTS_PATH_SEGMENTS.UPLOAD}`,
          handle: { breadcrumb: "Upload" },
          Component: CompanyDocumentUploadForm,
        },
      ],
    },

    // ── Request new (no stats, no tabs) ──────────────────────────────────────
    {
      path: COMPANY_DOCUMENTS_PATH_SEGMENTS.REQUEST,
      handle: { breadcrumb: "Request" },
      Component: CompanyDocumentRequestForm,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_DOCUMENTS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT)),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.DOCUMENTS.ROOT,
  SETTINGS_VTAB_DOC_TYPES: MODULE_SETTINGS_PATHS.DOCUMENTS.VTAB_DOC_TYPES,

  EMPLOYEE_DOCUMENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
    ),
  ),
  COMPANY_DOCUMENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
    ),
  ),
  SENT_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
    ),
  ),
  RECEIVED_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
    ),
  ),
  EMPLOYEE_DOCUMENT_DETAILS: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
        String(id),
      ),
    ),
  EMPLOYEE_DOCUMENT_NEW: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.NEW,
    ),
  ),
  EMPLOYEE_DOCUMENT_EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.EMPLOYEE_DOCUMENTS,
        String(id),
        COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT,
      ),
    ),
  COMPANY_DOCUMENT_NEW: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.NEW,
    ),
  ),
  COMPANY_DOCUMENT_VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
        String(id),
        COMPANY_DOCUMENTS_PATH_SEGMENTS.VIEW,
      ),
    ),
  COMPANY_DOCUMENT_EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.COMPANY_DOCUMENTS,
        String(id),
        COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT,
      ),
    ),
  REQUEST: withCompanyPrefix(
    addSlashes(
      COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
      COMPANY_DOCUMENTS_PATH_SEGMENTS.REQUEST,
    ),
  ),
  SENT_REQUEST_VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
        String(id),
      ),
    ),
  SENT_REQUEST_EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
        String(id),
        COMPANY_DOCUMENTS_PATH_SEGMENTS.EDIT,
      ),
    ),
  RECEIVED_REQUEST_VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
        String(id),
      ),
    ),
  RECEIVED_REQUEST_UPLOAD: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DOCUMENTS_PATH_SEGMENTS.ROOT,
        COMPANY_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
        String(id),
        COMPANY_DOCUMENTS_PATH_SEGMENTS.UPLOAD,
      ),
    ),
});
